import { useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { ContractApiResponse } from '../../types/api-responses';
import {
    ContractMetadata,
    extractMetadata,
    extractBusinessPartner,
    extractFilesFromSchema,
    extractContractGuaranteeFromSchema,
    ContractGuaranteeState,
    extractContractsAsReceivablesFromSchema,
    ContractsAsReceivablesState,
    ExistingFiles,
} from '../../utils/mappers/contractDataHelpers';
import { BusinessPartnerData } from '../../components/form/BusinessPartnerManager';

// ============================================
// TYPES
// ============================================

export interface FetchContractResult {
    data: ContractApiResponse;
    schema: any;
    metadata: ContractMetadata;
    businessPartner: BusinessPartnerData | null;
    files: ExistingFiles | null;
    contractGuarantee: ContractGuaranteeState | null;
    contractsAsReceivables: ContractsAsReceivablesState | null;
}

interface UseContractFetchParams {
    apiUrl: string;
    apiKey: string;
    creditOperationId?: string;
    setLoading: (v: boolean) => void;
    setHasUnsavedChanges: (v: boolean) => void;
    setIsContractLoaded: (v: boolean) => void;
}

interface UseContractFetchCallbacks {
    onMetadata?: (metadata: ContractMetadata) => void;
    onBusinessPartner?: (partner: BusinessPartnerData | null) => void;
    onFiles?: (files: ExistingFiles) => void;
    onContractGuarantee?: (guarantee: ContractGuaranteeState) => void;
    onContractsAsReceivables?: (contracts: ContractsAsReceivablesState) => void;
    onData: (result: FetchContractResult) => void;
}

// ============================================
// HOOK
// ============================================

export const useContractFetch = ({
    apiUrl,
    apiKey,
    creditOperationId,
    setLoading,
    setHasUnsavedChanges,
    setIsContractLoaded,
}: UseContractFetchParams) => {

    const fetchContract = useCallback(async (callbacks: UseContractFetchCallbacks) => {
        if (!creditOperationId || !apiUrl || !apiKey) return;

        try {
            setLoading(true);
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${apiUrl}/contracts/receivables_assignment/${creditOperationId}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': apiKey }
            });

            if (!response.ok) {
                toast.error('Erro ao carregar dados da operação');
                return;
            }

            const data: ContractApiResponse = await response.json();
            const schema = data.operation_dictionary || {};

            // Extract shared data
            const metadata = extractMetadata(data);
            const businessPartner = extractBusinessPartner(data);
            const files = extractFilesFromSchema(schema);
            const contractGuarantee = extractContractGuaranteeFromSchema(schema);
            const contractsAsReceivables = extractContractsAsReceivablesFromSchema(schema);

            // Apply shared callbacks
            if (callbacks.onMetadata) callbacks.onMetadata(metadata);
            if (callbacks.onBusinessPartner) callbacks.onBusinessPartner(businessPartner);
            if (files && callbacks.onFiles) callbacks.onFiles(files);
            if (contractGuarantee && callbacks.onContractGuarantee) callbacks.onContractGuarantee(contractGuarantee);
            if (contractsAsReceivables && callbacks.onContractsAsReceivables) callbacks.onContractsAsReceivables(contractsAsReceivables);

            // Delegate page-specific logic
            callbacks.onData({ data, schema, metadata, businessPartner, files, contractGuarantee, contractsAsReceivables });
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setHasUnsavedChanges(false);
            setIsContractLoaded(true);
        }
    }, [creditOperationId, apiUrl, apiKey, setLoading, setHasUnsavedChanges, setIsContractLoaded]);

    return { fetchContract };
};
